import * as Sentry from "@sentry/react";

// VITE_ vars are inlined by Vite at build time.
const dsn = import.meta.env.VITE_SENTRY_DSN || "";

const SENSITIVE_HEADER_KEYS = new Set([
  "accesstoken",
  "authorization",
  "apikey",
  "appkey",
  "xapikey",
  "xappkey",
]);

const normalizeHeaderKey = (key: string) =>
  key.toLowerCase().replace(/[-_\s]/g, "");

/** Remove auth/key headers regardless of casing or hyphenation. */
const stripSensitiveHeaders = (headers?: Record<string, unknown>) => {
  if (!headers || typeof headers !== "object") return;
  Object.keys(headers).forEach((key) => {
    if (SENSITIVE_HEADER_KEYS.has(normalizeHeaderKey(key))) {
      delete headers[key];
    }
  });
};

const sanitizeBreadcrumb = (crumb: any) => {
  if (!crumb?.data || typeof crumb.data !== "object") return crumb;

  // Strip direct header maps and the common nested request shape.
  stripSensitiveHeaders(crumb.data);
  stripSensitiveHeaders(crumb.data.headers);
  stripSensitiveHeaders(crumb.data.request?.headers);

  if (typeof crumb.data.url === "string" && crumb.data.url.includes("/api/")) {
    // Remove query params and body from API calls.
    delete crumb.data.body;
    delete crumb.data.input;
  }
  return crumb;
};

export const initSentry = () => {
  if (!dsn || dsn.includes("<key>")) {
    console.warn("[Sentry] DSN not configured. Skipping Sentry initialization.");
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: import.meta.env.MODE === "development" ? 1.0 : 0.2,
    replaysSessionSampleRate: import.meta.env.MODE === "development" ? 0.0 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],
    beforeSend: (event) => {
      if (import.meta.env.MODE === "production" && event.level === "debug") {
        return null;
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(sanitizeBreadcrumb);
      }
      // Remove sensitive headers (case-insensitive)
      stripSensitiveHeaders(event.request?.headers);
      return event;
    },
    initialScope: {
      tags: {
        platform: "web",
        appVersion: import.meta.env.MODE,
      },
    },
  });

  console.log("[Sentry] Initialized successfully");
};

export const setSentryUser = (userId: string, email?: string, role?: string) => {
  Sentry.setUser({ id: userId, email, role });
};

export const clearSentryUser = () => {
  Sentry.setUser(null);
};
